import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { downloadInvoicesZip, fetchInvoices } from '@/features/billing/billingThunks';
import {
  selectDownloadInvoicesZipError,
  selectDownloadInvoicesZipLoading,
  selectInvoices,
  selectInvoicesState,
} from '@/features/billing/billingSlice';
import type { BillingCurrency, StripeInvoiceDto } from '@/features/billing/types';
import { formatPrice, resolveBillingCurrency } from '@/components/billing/usageExpiredUtils';
import Title from '@/components/ui/Title';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { DownloadIcon } from 'lucide-react';
import { getLocale } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

// Les factures ne portent pas leur devise : ce sont toujours celles de la devise du workspace.
function getColumns(t: TFunction, currency: BillingCurrency): ColumnDef<StripeInvoiceDto>[] {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('invoices.selectAll')}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={t('invoices.selectRow')}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'number',
      header: () => <div className="text-left">{t('invoices.columns.invoiceNumber')}</div>,
      cell: ({ row }) => {
        const number = row.getValue('number') as string;
        return <div className="text-left">{number}</div>;
      },
    },
    {
      accessorKey: 'amountPaid',
      header: () => <div className="text-left">{t('invoices.columns.amount')}</div>,
      cell: ({ row }) => {
        const amountPaid = row.getValue('amountPaid') as number;
        return <div className="text-left font-medium">{formatPrice(amountPaid, currency)}</div>;
      },
    },
    {
      accessorKey: 'status',
      header: t('invoices.columns.status'),
      cell: ({ row }) => {
        const status = row.getValue('status') as string;
        const getStatusColor = (s: string) => {
          switch (s) {
            case 'paid':
              return 'bg-[#E6F5E2] text-[#3EB51E]';
            case 'open':
              return 'bg-yellow-100 text-yellow-800';
            case 'draft':
              return 'bg-gray-100 text-gray-800';
            case 'void':
              return 'bg-red-100 text-red-800';
            default:
              return 'bg-gray-100 text-gray-800';
          }
        };
        const statusLabel =
          status === 'paid'
            ? t('invoices.status.paid')
            : status === 'open'
              ? t('invoices.status.open')
              : status === 'draft'
                ? t('invoices.status.draft')
                : status === 'void'
                  ? t('invoices.status.void')
                  : status;

        return <Badge className={getStatusColor(status)}>{statusLabel}</Badge>;
      },
    },
    {
      accessorKey: 'created',
      header: () => <div className="text-left">{t('invoices.columns.date')}</div>,
      cell: ({ row }) => {
        const created = row.getValue('created') as string;
        const date = new Date(created);
        const formattedDate = date.toLocaleDateString(getLocale(), {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
        return <div className="text-left text-text-secondary">{formattedDate}</div>;
      },
    },
  ];
}

const EMPTY_INVOICES: StripeInvoiceDto[] = [];

export default function PaymentHistory() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('settings');
  const workspaceId = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.id);
  const currency = resolveBillingCurrency(
    useAppSelector((s: RootState) => s.workspace.currentWorkspace?.billingCurrency),
  );
  const invoices = useAppSelector(selectInvoices);
  const { loading, error } = useAppSelector(selectInvoicesState);

  useEffect(() => {
    if (workspaceId != null) {
      dispatch(fetchInvoices(workspaceId));
    }
  }, [workspaceId, dispatch]);

  const columns = useMemo(() => getColumns(t, currency), [t, currency]);

  const table = useReactTable({
    data: invoices ?? EMPTY_INVOICES,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    getRowId: (row) => row.id,
  });

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const selectedInvoices = selectedRows.map((row) => row.original);
  const hasSelection = selectedInvoices.length > 0;
  const downloadZipLoading = useAppSelector(selectDownloadInvoicesZipLoading);
  const downloadZipError = useAppSelector(selectDownloadInvoicesZipError);

  const handleDownloadPdf = async () => {
    if (!workspaceId || selectedInvoices.length === 0) return;
    if (selectedInvoices.length === 1) {
      const invoice = selectedInvoices[0];
      if (invoice.invoicePdfUrl) {
        window.open(invoice.invoicePdfUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    try {
      const blob = await dispatch(
        downloadInvoicesZip({
          workspaceId,
          invoiceIds: selectedInvoices.map((inv) => inv.id),
        }),
      ).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-${Date.now()}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('downloadInvoicesZip error', err);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">{t('invoices.loading')}</div>;
  if (error) return <div className="text-sm text-destructive">{error}</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <Title level={2}>{t('invoices.title')}</Title>
        <div className="flex flex-col items-start sm:items-end gap-1">
          {downloadZipError && <span className="text-sm text-destructive">{downloadZipError}</span>}
          <button
            disabled={!hasSelection || downloadZipLoading}
            onClick={() => void handleDownloadPdf()}
            className="flex items-center gap-2 text-text-secondary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DownloadIcon className="w-4 h-4" />
            {downloadZipLoading
              ? t('invoices.downloading')
              : `${t('invoices.download')}${hasSelection ? ` (${selectedInvoices.length})` : ''}`}
          </button>
        </div>
      </div>

      <div className="w-full">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {t('invoices.noInvoices')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
